import { create } from "zustand";
import { z } from "zod";
import { Case, Patient } from "@/API";

export const FormSchema = z.object({
  patientName: z.string().min(1, "Patient name is required"),
  clientName: z.string().min(1, "Client name is required"),
  caseTitle: z.string().min(1, "Case title is required"),
  caseDescription: z.string().optional(),
  useTemplate: z.boolean(),
  templateId: z.string().optional(),
});

export type FormValues = z.infer<typeof FormSchema>;

interface QuickRecordAddCaseStore {
  formData: FormValues;
  patients: Patient[];
  patientCases: Case[];
  touched: Partial<Record<keyof FormValues, boolean>>;
  setTouched: <K extends keyof FormValues>(field: K, value: boolean) => void;
  setField: <K extends keyof FormValues>(
    field: K,
    value: FormValues[K],
  ) => void;
  reset: () => void;
  setPatients: (patients: Patient[]) => void;
  setPatientCases: (patientCases: Case[]) => void;
}

const initialState: FormValues = {
  patientName: "",
  clientName: "",
  caseTitle: "",
  caseDescription: "",
  useTemplate: false,
  templateId: "",
};

export const useQuickRecordAddCaseStore = create<QuickRecordAddCaseStore>()(
  (set) => ({
    formData: initialState,
    patients: [],
    patientCases: [],
    touched: {},
    setTouched: (field, value: boolean) =>
      set((state) => ({
        touched: {
          ...state.touched,
          [field]: value,
        },
      })),
    setField: (field, value) =>
      set((state) => ({
        formData: {
          ...state.formData,
          [field]: value,
        },
      })),
    reset: () => set({ formData: initialState, touched: {} }),
    setPatients: (patients: Patient[]) => set({ patients }),
    setPatientCases: (patientCases: Case[]) => set({ patientCases }),
  }),
);
